/**
 * ProcessModelPanel — generates a best-in-class, sector/size/standards-tailored
 * Target Operating Process (Claude Opus, background job) and downloads it as an
 * editable swim-lane PPTX (+ RACI/KPI matrix + Target-Operating-Model on a page).
 * Mirrors the RunPlanPanel fire-and-poll pattern. Framework-agnostic.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Workflow, RefreshCw, FileDown, ShieldCheck, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "../../lib/api";
import GenerationProgress from "./GenerationProgress";

async function downloadProcessPptx(runId) {
  try {
    const res = await fetch(`${API_BASE}/admin/assessments/runs/${runId}/export/processmodel`, { credentials: "include" });
    if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
    const cd = res.headers.get("content-disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = m ? m[1] : "target-operating-process.pptx";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Swim-lane process (PPTX) downloaded");
  } catch (err) {
    toast.error(`Download failed: ${err.message || err}`);
  }
}

export default function ProcessModelPanel({ runId }) {
  const [pm, setPm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [lastError, setLastError] = useState(null);
  const pollRef = useRef(null);
  const toastRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/process-model`);
      setPm(data.process_model);
      setLastError(data.last_error || null);
      if (data.status === "generating") setGenerating(true);
    } catch (e) {
      if (e?.response?.status !== 404) toast.error(e?.response?.data?.detail || "Load failed");
      setPm(null);
    } finally { setLoading(false); }
  }, [runId]);

  useEffect(() => { load(); return () => stopPolling(); }, [load, stopPolling]);

  useEffect(() => {
    if (!generating || pollRef.current) return;
    const startedAt = Date.now();
    const MAX_POLL_MS = 9 * 60 * 1000;
    const tick = async () => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        stopPolling(); setGenerating(false);
        toast.error("Taking longer than expected. Refresh or regenerate.", { id: toastRef.current });
        toastRef.current = null; return;
      }
      try {
        const { data } = await api.get(`/admin/assessments/runs/${runId}/process-model`);
        setPm(data.process_model);
        setLastError(data.last_error || null);
        const st = data.status || data.process_model?.status || "ready";
        if (st === "ready") {
          stopPolling(); setGenerating(false);
          if (data.last_error) toast.error(data.last_error, { id: toastRef.current, duration: 8000 });
          else toast.success("Target operating process ready", { id: toastRef.current });
          toastRef.current = null;
        } else if (st === "failed") {
          stopPolling(); setGenerating(false);
          toast.error(data.last_error || data.process_model?.error || "Generation failed", { id: toastRef.current, duration: 8000 });
          toastRef.current = null;
        }
      } catch { /* transient */ }
    };
    pollRef.current = setInterval(tick, 4000);
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generating, runId]);

  const generate = useCallback(async () => {
    if (generating) return;
    setGenerating(true);
    const ts = toast.loading("Designing the target operating process (~2–3 min). You can switch tabs.");
    toastRef.current = ts;
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/process-model`, {
        force_regenerate: !!pm && pm.status === "ready",
      });
      setPm(data.process_model);
      if (data.cached) { setGenerating(false); toast.success("Loaded from cache", { id: ts }); }
      else if (data.status === "ready") { setGenerating(false); toast.success("Ready", { id: ts }); }
      else { toast.loading("Generating in the background…", { id: ts }); }
    } catch (e) {
      setGenerating(false);
      toast.error(e?.response?.data?.detail || e.message || "Generation failed", { id: ts });
    }
  }, [runId, pm, generating]);

  const pl = pm?.payload;
  const ready = pm?.status === "ready" || (pm && !pm.status);

  const [integrity, setIntegrity] = useState(null);
  const [checking, setChecking] = useState(false);
  const checkIntegrity = useCallback(async () => {
    setChecking(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/qa-check`);
      const tom = (data.checks || []).filter((c) => c.id.startsWith("tom_"));
      setIntegrity(tom);
      const fails = tom.filter((c) => !c.ok && c.severity === "error");
      if (fails.length) toast.error(`${fails.length} integrity issue(s) — fix before finalising`);
      else toast.success("Operating-model integrity checks pass");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Integrity check failed");
    } finally { setChecking(false); }
  }, [runId]);

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="process-model-panel">
      <div className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]">
        <Workflow size={14}/> Target operating process
      </div>
      <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
        A best-in-class, sector/size/standards-tailored future-state process as an <strong>editable swim-lane PPTX</strong>
        {" "}— plus a RACI/KPI matrix and a Target-Operating-Model on a page. Lanes are functional actors; each phase shows its accountable C-suite sponsor.
      </p>

      {pm?.status === "failed" && pm?.error && (
        <div className="text-[11px] text-red-700 mt-2">Generation failed — {pm.error}</div>
      )}

      {lastError && (
        <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-2 rounded" data-testid="process-last-error">
          Last regeneration didn't complete — {lastError} Your previous AI version is still shown below and used in exports.
        </div>
      )}

      <GenerationProgress active={generating} label="Designing the process" typical="~2–3 min" testid="process-generation-progress" />

      <button
        type="button"
        onClick={generate}
        disabled={generating}
        className="w-full mt-3 bg-[#7737F6] hover:bg-[#5D28C7] disabled:opacity-60 text-white text-[13px] font-medium px-3 py-2 flex items-center justify-center gap-2 transition-colors"
        data-testid="process-generate"
      >
        {generating ? <RefreshCw size={14} className="animate-spin"/> : <Workflow size={14}/>}
        {generating ? "Generating…" : pm && ready ? "Regenerate process" : "Generate target process"}
      </button>

      {ready && pl && (
        <div className="mt-4 space-y-3">
          <div className="border-l-2 border-[#7737F6] pl-3">
            <div className="text-[13px] font-medium text-zinc-900">{pl.process_name}</div>
            <p className="text-[12px] text-zinc-600 mt-1">{pl.summary}</p>
            {pl.standards?.length > 0 && (
              <div className="text-[11px] text-zinc-500 mt-1">Anchored to: {pl.standards.join(", ")}</div>
            )}
            <div className="text-[11px] text-zinc-400 mt-1">
              {pl.lanes?.length || 0} lanes · {pl.phases?.length || 0} phases · {pl.steps?.length || 0} steps
              {pm?.payload?._skeleton ? " · deterministic draft" : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={() => downloadProcessPptx(runId)}
            className="w-full text-left flex items-center justify-between px-3 py-2.5 border-2 border-[#7737F6] bg-[#7737F6] text-white text-[13px] font-medium hover:bg-[#5f29c9] transition-colors"
            data-testid="process-download-pptx"
          >
            <span>Download swim-lane process (PPTX) — editable</span>
            <FileDown size={14} className="text-white"/>
          </button>
          <p className="text-[10.5px] text-zinc-500">It also auto-appends a "Target operating process" section to the Blueprint DOCX.</p>

          <div className="border-t border-zinc-200 pt-3">
            <button
              type="button"
              onClick={checkIntegrity}
              disabled={checking}
              className="w-full bg-[#0B0B0C] hover:bg-[#26262c] disabled:opacity-60 text-white text-[12px] font-medium px-3 py-2 flex items-center justify-center gap-2 transition-colors"
              data-testid="process-integrity-check"
            >
              {checking ? <RefreshCw size={13} className="animate-spin"/> : <ShieldCheck size={13}/>}
              {checking ? "Checking…" : "Check model integrity"}
            </button>
            <p className="text-[10.5px] text-zinc-400 mt-1">Flags any step/handoff/RACI/decision that won't reconcile — before you run the full QA gate.</p>
            {integrity && (
              <ul className="mt-2 space-y-1" data-testid="process-integrity-report">
                {integrity.map((c) => (
                  <li key={c.id} className="flex items-start gap-2 text-[11.5px]" data-testid={`process-integrity-${c.id}`}>
                    {c.ok ? <CheckCircle2 size={13} className="text-emerald-600 shrink-0 mt-0.5"/>
                      : c.severity === "error" ? <XCircle size={13} className="text-red-600 shrink-0 mt-0.5"/>
                      : <AlertTriangle size={13} className="text-amber-600 shrink-0 mt-0.5"/>}
                    <span className="text-zinc-600"><span className={c.ok ? "" : c.severity === "error" ? "text-red-700 font-medium" : "text-amber-800"}>{c.label}</span><span className="block text-[10.5px] text-zinc-400">{c.detail}</span></span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
